
///
/// EarthBrowser - Global Search Hook
///
/// Created: 2025-11-16
/// Copyright (c) 2025 Geodata Labs
///

import type { CommandItem } from "@/components/controls/layers-panel/commandTypes"
import { Constants } from "@/constants"
import { Database } from "@/earthbrowser/database/Database"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch } from "@/redux/store"
import type { VolcanoInfo } from "@shared-types/volcano-info"
import type { EarthquakeEntry } from "@shared-types/data"
import type { SatelliteEntry } from "@shared-types/satellite-entry"
import { Activity, Landmark, Mountain, Satellite } from "lucide-react"
import React, { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

interface LocationEntry {
	geoid: number,
	name: string,
	admin: string,
	country: string,
	tz: string,
	pop: number,
	lat: number,
	lon: number,
	alt: number,
	country_name: string,
	admin_name: string,
}

export interface SearchResultItem {
	id: string
	type: 'satellite' | 'earthquake' | 'volcano' | 'location'
	title: string
	subtitle: string
	image_url?: string
	data: SatelliteEntry | EarthquakeEntry | VolcanoInfo | LocationEntry
	enabled: boolean
	keywords: string[]
}

interface SearchResult {
	satellites: SearchResultItem[]
	earthquakes: SearchResultItem[]
	volcanoes: SearchResultItem[]
	locations: SearchResultItem[]
	loading: boolean
}

interface SearchCommandsResult {
	satellites: CommandItem[]
	earthquakes: CommandItem[]
	volcanoes: CommandItem[]
	locations: CommandItem[]
	loading: boolean
}

export function useGlobalSearch(query: string, enabled: boolean = true): SearchResult {
	const [satellites, setSatellites] = useState<SearchResultItem[]>([])
	const [earthquakes, setEarthquakes] = useState<SearchResultItem[]>([])
	const [volcanoes, setVolcanoes] = useState<SearchResultItem[]>([])
	const [locations, setLocations] = useState<SearchResultItem[]>([])
	const [loading, setLoading] = useState(false)

	const performSearch = useCallback(async (searchQuery: string) => {
		if (!searchQuery || searchQuery.length < 2) {
			setSatellites([])
			setEarthquakes([])
			setVolcanoes([])
			setLocations([])
			return
		}

		setLoading(true)

		try {
			// Resolve the database at search time: it may still have been
			// initializing when this hook first rendered
			const database = Database.getDb()

			// Run all searches in parallel for better performance
			if (database) {
				const [satelliteResults, earthquakeResults, volcanoResults, locationResults] = await Promise.all([
					searchSatellites(database, searchQuery),
					searchEarthquakes(database, searchQuery),
					searchVolcanoes(database, searchQuery),
					searchLocations(database, searchQuery)
				])

				setSatellites(satelliteResults)
				setEarthquakes(earthquakeResults)
				setVolcanoes(volcanoResults)
				setLocations(locationResults)
			}
		} catch (error) {
			console.error("Search error:", error)
		} finally {
			setLoading(false)
		}
	}, [])

	// Debounce search - increased for better performance
	useEffect(() => {
		if (!enabled) return

		const timer = setTimeout(() => {
			void performSearch(query)
		}, 400)

		return () => clearTimeout(timer)
	}, [query, enabled, performSearch])

	return {
		satellites,
		earthquakes,
		volcanoes,
		locations,
		loading
	}
}

async function searchSatellites(database: Database, query: string): Promise<SearchResultItem[]> {
	try {
		const lowerQuery = query.toLowerCase()
		const startsWithPattern = `${lowerQuery}%`
		const containsPattern = `%${lowerQuery}%`

		// Search the new satellite_tle table with case-insensitive matching
		const results = await database.exec(
			`SELECT
				object_id,
				object_name,
				norad_cat_id
			FROM satellite_tle
			WHERE object_name LIKE ? COLLATE NOCASE
			ORDER BY
				CASE WHEN object_name LIKE ? COLLATE NOCASE THEN 0 ELSE 1 END,
				norad_cat_id ASC
			LIMIT 10`,
			[containsPattern, startsWithPattern],
			"critical"
		) as SatelliteEntry[]

		if (!results || results.length === 0) return []

		return results.map((sat) => ({
			id: `satellite-${sat.object_id}`,
			type: 'satellite' as const,
			title: sat.object_name,
			subtitle: `NORAD ${sat.norad_cat_id}`,
			image_url: undefined,
			data: sat,
			enabled: true,
			keywords: [sat.object_name.toLowerCase()]
		}))
	} catch (error) {
		console.error("Satellite search error:", error)
		return []
	}
}

async function searchEarthquakes(database: Database, query: string): Promise<SearchResultItem[]> {
	try {
		const lowerQuery = query.toLowerCase()
		const startsWithPattern = `${lowerQuery}%`
		const containsPattern = `%${lowerQuery}%`

		// Case-insensitive search using LOWER() on both sides
		const response = await database.exec(
			`SELECT
				id,
				place,
				mag,
				title
			FROM earthquakes
				WHERE LOWER(place) LIKE ?
			ORDER BY
				CASE WHEN LOWER(place) LIKE ? THEN 0 ELSE 1 END,
				mag DESC
			LIMIT 10`,
			[containsPattern, startsWithPattern],
			"critical"
		) as EarthquakeEntry[]

		if (!response || response.length === 0) return []

		return response.map((quake) => ({
			id: `earthquake-${quake.id}`,
			type: 'earthquake' as const,
			title: quake.title || quake.place || 'Unknown location',
			subtitle: `Magnitude ${quake.mag.toFixed(1)}`,
			image_url: undefined,
			data: quake,
			enabled: true,
			keywords: [quake.place?.toLowerCase() || '']
		}))
	} catch (error) {
		console.error("Earthquake search error:", error)
		return []
	}
}

async function searchVolcanoes(database: Database, query: string): Promise<SearchResultItem[]> {
	try {
		const lowerQuery = query.toLowerCase()
		const startsWithPattern = `${lowerQuery}%`
		const containsPattern = `%${lowerQuery}%`

		// Case-insensitive search using LOWER() on both sides
		const response = await database.exec(
			`SELECT
				id as id,
				number as number,
				name as name,
				type as type,
				country as country,
				region as region,
				elevation_m as elevation,
				lat as latitude,
				lon as longitude,
				last_eruption as lastEruption,
				subregion as subregion,
				epoch as epoch
			FROM volcanoes
			WHERE LOWER(name) LIKE ?
			ORDER BY
				CASE WHEN LOWER(name) LIKE ? THEN 0 ELSE 1 END,
				name ASC
			LIMIT 10`,
			[containsPattern, startsWithPattern],
			"critical"
		) as (VolcanoInfo & { id: string })[]

		if (!response || response.length === 0) return []

		return response.map((volcano) => ({
			id: `volcano-${volcano.id}`,
			type: 'volcano' as const,
			title: volcano.name,
			subtitle: `${volcano.type}, ${volcano.country}`,
			image_url: undefined,
			data: volcano,
			enabled: true,
			keywords: [volcano.name.toLowerCase(), volcano.country.toLowerCase(), volcano.region.toLowerCase()]
		}))
	} catch (error) {
		console.error("Volcano search error:", error)
		return []
	}
}

async function searchLocations(database: Database, query: string): Promise<SearchResultItem[]> {
	try {
		const lowerQuery = query.toLowerCase()
		const startsWithPattern = `${lowerQuery}%`
		const containsPattern = `%${lowerQuery}%`

		// Case-insensitive search using LOWER() on both sides
		const sql = `
		SELECT
			l.geoid, l.name, l.country, l.admin1, l.admin2, l.tz, l.pop, l.lat, l.lon, l.alt, l.code,
			c.name as country_name, ca.name as admin_name
		FROM locations l
		JOIN countries c ON l.country = c.country_code
		JOIN country_admin ca ON l.country = ca.country_code AND l.admin1 = ca.admin_code
		WHERE LOWER(l.name) LIKE ?
		ORDER BY
			CASE WHEN LOWER(l.name) LIKE ? THEN 0 ELSE 1 END,
			l.pop DESC
		LIMIT 15`
		const results = await database.exec(
			sql,
			[containsPattern, startsWithPattern],
			"critical"
		) as LocationEntry[]

		if (!results || results.length === 0) return []

		return results.map((loc) => ({
			id: `location-${loc.geoid}`,
			type: 'location' as const,
			title: loc.name,
			subtitle: `${loc.country_name}, ${loc.admin_name}`,
			image_url: `${Constants.baseFlagUrl}/${loc.country.toLowerCase()}.svg`,
			data: loc,
			enabled: true,
			keywords: [loc.name.toLowerCase(), loc.country_name.toLowerCase(), loc.admin_name.toLowerCase()]
		}))
	} catch (error) {
		console.error("Location search error:", error)
		return []
	}
}

/**
 * Custom hook that converts search results to CommandItems with proper actions and icons
 * This bridges the data layer (useGlobalSearch) with the UI layer (CommandPalette)
 */
export function useGlobalSearchCommands(query: string, enabled: boolean = true): SearchCommandsResult {
	const dispatch = useAppDispatch()
	const [, setSearchParams] = useSearchParams()
	const searchResults = useGlobalSearch(query, enabled)

	const setTarget = useCallback((value: string) => {
		setSearchParams(prev => {
			const newParams = new URLSearchParams(prev)
			newParams.set("target", value)
			return newParams
		})
	}, [setSearchParams])

	const toCommandItems = useCallback((results: SearchResultItem[]): CommandItem[] => {
		return results.map(result => {
			// Get icon based on type
			let icon: React.ReactNode
			switch (result.type) {
				case 'satellite':
					icon = React.createElement(Satellite, { className: "w-4 h-4 text-blue-400" })
					break
				case 'earthquake':
					icon = React.createElement(Activity, { className: "w-4 h-4 text-red-500" })
					break
				case 'volcano':
					icon = React.createElement(Mountain, { className: "w-4 h-4 text-orange-500" })
					break
				case 'location':
					icon = React.createElement(Landmark, { className: "w-4 h-4 text-green-400" })
					break
			}

			// Extract actual ID from prefixed ID format
			const actualId = result.id.replace(/^(satellite|earthquake|volcano|location)-/, '')

			// Determine target prefix based on type
			let targetPrefix: string
			switch (result.type) {
				case 'satellite':
					targetPrefix = 'sat'
					break
				case 'earthquake':
					targetPrefix = 'quake'
					break
				case 'volcano':
					targetPrefix = 'volcano'
					break
				case 'location':
					targetPrefix = 'geoid'
					break
			}

			// City results carry their coordinate so the row can offer the
			// inline "Add placemark" action alongside fly-to
			const place = result.type === 'location'
				? (() => {
					const loc = result.data as LocationEntry
					return { lat: loc.lat, lon: loc.lon, name: loc.name }
				})()
				: undefined

			return {
				id: result.id,
				type: result.type,
				title: result.title,
				subtitle: result.subtitle,
				icon,
				image_url: result.image_url,
				place,
				action: () => {
					// Dispatch proper selection action based on type
					dispatch(sendDatasetAction({
						type: result.type,
						action: "selectId",
						id: actualId
					}))
					// Set URL target parameter for camera navigation
					setTarget(`${targetPrefix}-${actualId}`)
				},
				enabled: result.enabled,
				keywords: result.keywords
			}
		})
	}, [dispatch, setTarget])

	return {
		satellites: toCommandItems(searchResults.satellites),
		earthquakes: toCommandItems(searchResults.earthquakes),
		volcanoes: toCommandItems(searchResults.volcanoes),
		locations: toCommandItems(searchResults.locations),
		loading: searchResults.loading
	}
}
